import { Releases, Sort } from 'constants/filters'
import { BoolType } from 'constants/resources'
import { captureError } from 'helpers/error'
import { initFilters } from 'helpers/filters'
import { removeEmpty } from 'helpers/objects'
import {
  action,
  computed,
  makeAutoObservable,
  observable,
  runInAction,
} from 'mobx'
import qs from 'qs'
import { ExtendedRequest, ReviewItemFilter } from 'types'
import { GetApiV1AdminResourceChangeRequestsQueryParams } from 'types/api'
import Api from 'util/api'
import { DIContainer } from '../util/di'
import AuthStore from './AuthStore'
import GeneralReviewStore from './GeneralReviewStore'
import ResourceStore from './ResourceStore'

export default class RequestedReviewStore {
  api: Api
  AuthStore: AuthStore
  ResourceStore: ResourceStore
  GeneralReviewStore: GeneralReviewStore
  defaultFilter: ReviewItemFilter = {
    sort: Sort.Oldest,
    license: undefined,
    file_type: [],
    category_ids: [],
    content_type: [],
    program_type: undefined,
    history: undefined,
    releases: undefined,
    ai_generated: [],
    linked_asset: undefined,
    auto_submitted: [],
    magic_metadata: [],
    auto_distribute: [],
    duplicate: [],
    request_types: [],
  }

  @observable filter: ReviewItemFilter = initFilters<ReviewItemFilter>(
    this.defaultFilter
  )
  @observable loadingRequestedResources = false
  @observable requests: ExtendedRequest[] = []
  @observable selectedRequests: string[] = []
  @observable selectedRequest: ExtendedRequest | undefined = undefined
  @observable submittingRequests = false
  @observable infoModalVisibility = false
  @observable rejectModalVisibility = false
  @observable lastSelectedIndex = -1
  @observable rejectionComment = ''
  @observable rejectMultipleRequests: boolean | undefined = false

  constructor(container: DIContainer<Record<string, unknown>>) {
    makeAutoObservable(this)
    this.api = container.find('api')
    this.AuthStore = container.find('AuthStore')
    this.ResourceStore = container.find('ResourceStore')
    this.GeneralReviewStore = container.find('GeneralReviewStore')
  }

  @action reset = (): void => {
    this.resetFilters()
    this.ResourceStore.set([])
    this.ResourceStore.deselectAll()
    this.ResourceStore.setOriginal([])
  }

  @action setFilter = (filter: ReviewItemFilter, syncUrl = false): void => {
    this.ResourceStore.deselectAll()
    this.filter = filter
    if (syncUrl) {
      const url = qs.stringify(removeEmpty(this.filter), {
        encode: false,
        arrayFormat: 'comma',
        addQueryPrefix: true,
      })
      history.pushState({}, '', url)
    }
  }

  @action setFilterNonce = () => {
    const filter = { ...this.filter }
    filter.nonce = Math.random().toString(10)
    this.setFilter(filter, true)
  }

  @action resetFilters = (): void => {
    this.ResourceStore.deselectAll()
    this.setFilter(this.defaultFilter, true)
  }

  @action fetchRequestedResources = async (
    contributorId: string,
    query: GetApiV1AdminResourceChangeRequestsQueryParams
  ): Promise<void> => {
    const session = this.AuthStore.session
    if (session) {
      try {
        this.loadingRequestedResources = true
        const response = await this.api.request(
          'GET',
          `/api/v1/admin/resource_change_requests`,
          {
            query: {
              ...query,
              contributor_import_guid: contributorId,
            },
          }
        )
        runInAction(() => {
          this.requests = response.map((request: ExtendedRequest) =>
            observable(request)
          )
        })
      } catch (error) {
        captureError(
          error,
          'Error while fetching resource change requests - stores/RequestedReviewStore.ts'
        )
        throw error
      } finally {
        runInAction(() => {
          setTimeout(() => {
            this.loadingRequestedResources = false
          }, 500)
        })
      }
    }
  }

  @action rejectRequest = (requestId?: number, comment?: string): void => {
    if (!requestId || !comment) return

    const index = this.requests.findIndex(r => r.id === requestId)

    if (index !== -1) {
      const updatedRequest = {
        ...this.requests[index],
        review_result: 'rejected' as ExtendedRequest['review_result'],
        rejection_comment: comment,
      }

      this.requests.splice(index, 1, updatedRequest)
    }
  }

  @action approveRequest = (requestId?: number): void => {
    if (!requestId) return

    const index = this.requests.findIndex(r => r.id === requestId)

    if (index !== -1) {
      const updatedRequest = {
        ...this.requests[index],
        review_result: 'approved' as ExtendedRequest['review_result'],
        rejection_comment: undefined,
      }

      this.requests.splice(index, 1, updatedRequest)
    }
  }

  @action approveSelectedRequests = (): void => {
    if (this.selectedRequests.length === 0) {
      return
    }

    const requests = this.requests.filter(request =>
      this.selectedRequests.includes(String(request.id))
    )

    requests.forEach(request => {
      const index = this.requests.findIndex(r => r.id === request.id)
      const updatedRequest = {
        ...this.requests[index],
        review_result: 'approved' as ExtendedRequest['review_result'],
        rejection_comment: undefined,
      }

      this.requests.splice(index, 1, updatedRequest)
    })
  }

  @action rejectSelectedRequests = (comment?: string): void => {
    if (this.selectedRequests.length === 0) {
      return
    }

    const requests = this.requests.filter(request =>
      this.selectedRequests.includes(String(request.id))
    )

    requests.forEach(request => {
      const index = this.requests.findIndex(r => r.id === request.id)
      const updatedRequest = {
        ...this.requests[index],
        review_result: 'rejected' as ExtendedRequest['review_result'],
        rejection_comment: comment,
      }

      this.requests.splice(index, 1, updatedRequest)
    })
  }

  @action resetSelectedRequests = (): void => {
    if (this.selectedRequests.length === 0) {
      return
    }

    const requests = this.requests.filter(request =>
      this.selectedRequests.includes(String(request.id))
    )

    requests.forEach(request => {
      const index = this.requests.findIndex(r => r.id === request.id)
      const updatedRequest = {
        ...this.requests[index],
        review_result: undefined,
        rejection_comment: undefined,
      }

      this.requests.splice(index, 1, updatedRequest)
    })
  }

  @action resetRequestReview = (requestId?: number): void => {
    if (!requestId) return

    const index = this.requests.findIndex(r => r.id === requestId)

    if (index !== -1) {
      const updatedRequest = {
        ...this.requests[index],
        review_result: undefined,
      }

      this.requests.splice(index, 1, updatedRequest)
    }
  }

  @action deselectRequests = () => {
    this.selectedRequests = []
  }

  @action selectRequests = () => {
    const selectedRequests = this.requests.map(request => String(request.id))

    this.selectedRequests = selectedRequests
  }

  @action selectRequest = (requestId?: number, shiftPressed?: boolean) => {
    if (requestId === undefined) return

    const currentIndex = this.requests.findIndex(r => r.id === requestId)
    if (currentIndex === -1) return

    if (shiftPressed && this.lastSelectedIndex !== -1) {
      const startIndex = Math.min(this.lastSelectedIndex, currentIndex)
      const endIndex = Math.max(this.lastSelectedIndex, currentIndex)
      const newSelectedRequests = this.requests
        .slice(startIndex, endIndex + 1)
        .map(request => String(request.id))
      this.selectedRequests = newSelectedRequests
    } else {
      this.selectedRequests = [String(requestId)]
      this.lastSelectedIndex = currentIndex
    }
  }

  @action resetRequests = () => {
    const requestsCopy = [...this.requests]

    requestsCopy.forEach(request => {
      request.review_result = undefined
      request.rejection_comment = undefined
    })

    this.selectedRequests = []
    this.requests = requestsCopy
  }

  @action resetRequestedReviewPage = () => {
    this.selectedRequests = []
    this.requests = []
  }

  @action submitRequests = async () => {
    const selectedIds = this.selectedRequests.map(Number)

    const validRequests = selectedIds
      .map(id => this.getRequest(String(id)))
      .filter(
        request => request && request.review_result != null
      ) as ExtendedRequest[]

    if (validRequests.length === 0) {
      console.warn('No requests with review_result to submit.')
      return
    }

    try {
      this.submittingRequests = true

      const resourceChangeRequests = validRequests.map(request => ({
        id: request.id,
        review_result: request.review_result,
        rejection_comment: request.rejection_comment || '',
      }))

      await this.api.request(
        'POST',
        `/api/v1/admin/resource_change_requests/review`,
        { body: { resource_change_requests: resourceChangeRequests } }
      )

      const submittedIds = validRequests.map(request => request.id)
      runInAction(() => {
        this.requests = this.requests.filter(
          request => !submittedIds.includes(request.id)
        )
        this.selectedRequests = this.selectedRequests.filter(
          id => !submittedIds.includes(Number(id))
        )
        this.selectedRequest = undefined
      })
    } catch (error) {
      captureError(
        error,
        'Error while submitting requests - stores/RequestedReviewStore.ts'
      )
      throw error
    } finally {
      runInAction(() => {
        this.submittingRequests = false
      })
    }
  }

  @action toggleInfoModal = (request?: ExtendedRequest) => {
    if (request) {
      this.selectedRequest = request
    }
    this.infoModalVisibility = !this.infoModalVisibility
  }

  @action toggleRejectModal = (
    request?: ExtendedRequest,
    isMultple?: boolean
  ) => {
    if (request) {
      this.selectedRequest = request
    }

    this.rejectMultipleRequests = isMultple
    this.rejectModalVisibility = !this.rejectModalVisibility
    this.rejectionComment = ''
  }

  @action getRequest = (id: string) => {
    return this.requests.find(r => r.id === Number(id))
  }

  @action setRejectionComment = (comment: string) => {
    this.rejectionComment = comment
  }

  @computed get requestsNoteReadyForReview(): boolean {
    const selected = this.selectedRequests.map(r => this.getRequest(r))
    return !selected.some(r => r?.review_result)
  }

  @computed get queryParams(): GetApiV1AdminResourceChangeRequestsQueryParams {
    const params: GetApiV1AdminResourceChangeRequestsQueryParams = {
      contributor_import_guid: '',
    }

    if (this.filter.file_type?.length) {
      params.file_type = this.filter.file_type
    }

    if (this.filter.category_ids?.length) {
      params.category_ids = this.filter.category_ids
    }

    if (this.filter.program_type) {
      params.program_type = this.filter
        .program_type as GetApiV1AdminResourceChangeRequestsQueryParams['program_type']
    }

    if (this.filter.license) {
      params.license = [
        this.filter.license,
      ] as GetApiV1AdminResourceChangeRequestsQueryParams['license']
    }

    if (this.filter.sort === Sort.Newest) {
      params.sort_direction = 'desc'
    }

    if (this.filter.sort === Sort.Oldest) {
      params.sort_direction = 'asc'
    }

    if (this.filter.releases === Releases.With) {
      params.has_release = true
    }

    if (this.filter.releases === Releases.Without) {
      params.has_release = false
    }

    if (this.filter.ai_generated?.length) {
      const arr = this.filter.ai_generated

      if (arr.includes(BoolType.True) && arr.includes(BoolType.False)) {
        params.ai_generated = undefined
      } else if (arr.includes(BoolType.True)) {
        params.ai_generated = true
      } else {
        params.ai_generated = false
      }
    }

    if (this.filter.auto_submitted?.length) {
      const arr = this.filter.auto_submitted

      if (arr.includes(BoolType.True) && arr.includes(BoolType.False)) {
        params.auto_submitted = undefined
      } else if (arr.includes(BoolType.True)) {
        params.auto_submitted = true
      } else {
        params.auto_submitted = false
      }
    }

    if (this.filter.request_types?.length) {
      params.request_types = this.filter.request_types
    }

    return params
  }
}
